import { type UseChatOptions, useChat } from '@ai-sdk/react';
import { type AssistantRuntime, useAuiState } from '@assistant-ui/react';
import { AssistantChatTransport, type UseChatRuntimeOptions, useAISDKRuntime } from '@assistant-ui/react-ai-sdk';
import { lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';
import { use } from 'react';

import { FlowContext } from '#plugin-chat/contexts/FlowContext.ts';
import { useThreadHistoryAdapter } from '#plugin-chat/hooks/useThreadHistoryAdapter.ts';
import { useThreadSnapshotPersistence } from '#plugin-chat/hooks/useThreadSnapshotPersistence.ts';
import { type FlowResolverEntry, signalResolver } from '#plugin-chat/lib/flow/index.ts';
import { resolveFlow } from '#plugin-chat/lib/flow/resolveFlow.ts';
import { createTranscript } from '#plugin-chat/lib/flow/transcriptAdapter.ts';
import { AtlasChatTransport } from '#plugin-chat/lib/transport/AtlasChatTransport.tsx';
import { type PendingToolCall, pendingToolCalls } from '#plugin-chat/lib/transport/pendingToolCalls.ts';

/**
 * Compose the registered turn-flow resolvers with the always-on built-in
 * {@link signalResolver} (priority 0). The signal resolver is ALWAYS
 * evaluated so tool-emitted `flowSignal`s steer the turn even before any
 * declarative `flow()` is registered. Both seams that fold the engine —
 * the request side (bridged onto the transport via `setFlowResolvers`)
 * and the continuation side (`shouldContinueAgentLoop`) — resolve over
 * this same set, so the composition lives here and nowhere else.
 */
function engineResolvers(registered: readonly FlowResolverEntry[]): FlowResolverEntry[] {
  return [signalResolver(), ...registered];
}

/**
 * Pure predicate for the AI SDK's `sendAutomaticallyWhen` agentic tool loop.
 *
 * The loop must NOT continue once the user has requested a stop. The SDK's
 * own `stop()` only aborts a live `activeResponse`; clicked in the gap
 * between two requests (status transiently `ready`, `activeResponse`
 * cleared) it does nothing, and the queued continuation would otherwise
 * re-submit. Gating the loop on explicit stop intent (owned by
 * `AtlasChatTransport`) closes that race regardless of whether the
 * in-flight tool finished with an error or cleanly.
 *
 * The turn-flow engine also gets a vote: `resolvers` are folded over the
 * transcript via {@link resolveFlow}, and a `'stop'` continuation (from a
 * terminal `flow()` state or a tool-emitted `flowSignal`) gates the loop off
 * just like an explicit stop. Otherwise the existing "assistant completed
 * tool calls" continuation stands.
 */
export function shouldContinueAgentLoop({
  stopRequested,
  messages,
  resolvers,
}: {
  stopRequested: boolean;
  messages: UIMessage[];
  resolvers: FlowResolverEntry[];
}): boolean {
  if (stopRequested) {
    return false;
  }
  const { continuation } = resolveFlow({ transcript: createTranscript(messages), resolvers });
  if (continuation === 'stop') {
    return false;
  }
  return lastAssistantMessageIsCompleteWithToolCalls({ messages });
}

/**
 * Abort any dangling `requires-action` interrupt on the trailing
 * assistant message (`requestStop` → awaited `stop`), and — only when
 * `resolveCancelled` is supplied — settle each pending tool call as a
 * structured "cancelled" output-error.
 *
 * Settling is OPTIONAL because the two user-initiated seams differ in who
 * owns the transcript at cancel time:
 *
 *   - ESC / stop button: assistant-ui's `onCancel` delegates straight to
 *     our `stop` wrapper without touching the transcript, so WE must
 *     settle the dangling calls. Otherwise the trailing assistant message
 *     stays unsettled (`requires-action`), assistant-ui's settled-only
 *     `useExternalHistory` persist skips it, and on reload the whole
 *     message + its parts vanish (regression `65cba469`). This seam
 *     passes `resolveCancelled`.
 *
 *   - Superseding `sendMessage` (new / edit / reload): assistant-ui has
 *     ALREADY neutralised the pending call before our wrapper runs —
 *     `onNew` settles it via `setMessages`, `onEdit`/`onReload` truncate
 *     it out of the active branch. Re-settling here is not just redundant:
 *     the call we detect from `chat.messages` (a stale render snapshot)
 *     may no longer exist in the live store, so `addToolOutput` would
 *     deref a missing trailing message — a first-message edit empties the
 *     branch entirely (`TypeError: lastMessage is undefined`). This seam
 *     OMITS `resolveCancelled` and stop-only; the next run persists the
 *     settled state via the normal path.
 *
 * Returns the calls that were pending (empty when there was none — the
 * caller then performs an ordinary stop). When non-empty the run has been
 * stopped; distinct `toolCallId`s ⇒ any resolutions are independent and
 * safe to apply concurrently.
 */
export async function cancelPendingInterrupts(deps: {
  messages: readonly UIMessage[];
  requestStop: () => void;
  stop: () => Promise<void>;
  // Optional: only the ESC/stop seam settles (see docstring). AI SDK's
  // `addToolOutput` is typed `void | PromiseLike<void>`; `Promise.all`
  // below absorbs either, so accept the wider return here.
  resolveCancelled?: (call: PendingToolCall) => PromiseLike<void> | void;
}): Promise<PendingToolCall[]> {
  const pending = pendingToolCalls(deps.messages);
  if (pending.length === 0) {
    return [];
  }
  deps.requestStop();
  await deps.stop();
  if (deps.resolveCancelled) {
    await Promise.all(pending.map(deps.resolveCancelled));
  }
  return pending;
}
/**
 * Creates a chat thread runtime with automatic tool execution and error handling.
 * Wraps AI SDK's useChat with stop/cancel seams that settle dangling interrupts.
 */
export function useChatThreadRuntime({
  adapters,
  transport,
  toCreateMessage,
  ...chatOptions
}: UseChatRuntimeOptions<UIMessage> = {}): AssistantRuntime {
  const id = useAuiState(({ threadListItem }) => threadListItem.id);

  // The history adapter restores the full branch tree on load and exposes
  // `snapshot` for the single persistence writer (see
  // `useThreadSnapshotPersistence` below).
  //
  // Object identity must be stable across re-renders for the same
  // `id`: `useExternalHistory` (vendored) re-`useMemo`s `formatAdapter`
  // on `historyAdapter` change, and its subscribe `useEffect` then
  // tears down between schedule and fire of the debounced persist
  // `setTimeout(0)` — silently dropping the user message on a
  // mid-turn refresh. A bare `createThreadHistoryAdapter(id)` inline
  // depended on the React Compiler to memoize the call; the compiler
  // can bail out so the invariant must not rest on it.
  // `useThreadHistoryAdapter` enforces id-keyed identity stability
  // (`useState` + render-time mismatch reset; the `useChatSession`
  // pattern).
  const historyAdapter = useThreadHistoryAdapter(id);

  // The agentic tool loop is driven by the AI SDK's `sendAutomaticallyWhen`
  // continuation. Stop intent lives on the transport (a long-lived,
  // non-React run-lifecycle owner) so it survives the inter-request gap
  // where the SDK's own `stop()` early-returns and does nothing.
  const stopTransport = transport instanceof AtlasChatTransport ? transport : undefined;

  // The turn-flow engine's resolvers are registered in React (FlowContext).
  // Compose them with the always-on built-in signal resolver once here so the
  // same set drives both seams: the request side (bridged onto the non-React
  // transport via `setFlowResolvers`) and the continuation side (passed into
  // `shouldContinueAgentLoop`).
  const flowResolvers = engineResolvers(use(FlowContext).getFlowResolvers());
  stopTransport?.setFlowResolvers(flowResolvers);

  const chat = useChat({
    // UseChatRuntimeOptions extends UseChatOptions but with stricter generic constraints.
    // The spread works at runtime but TS can't infer the generic parameter compatibility.
    ...(chatOptions as Partial<UseChatOptions<UIMessage>>),
    id,
    transport,
    // Continue the agentic loop only when the assistant completed tool
    // calls AND the user has not requested a stop.
    sendAutomaticallyWhen: ({ messages: loopMessages }) =>
      shouldContinueAgentLoop({
        stopRequested: stopTransport?.stopRequested ?? false,
        messages: loopMessages,
        resolvers: flowResolvers,
      }),
    // Persistence is handled by the assistant-ui-native history adapter
    // (`adapters.history` below), which preserves the full branch tree.
    // Do NOT seed `messages` here or save in `onFinish`: that round-trips
    // the AI SDK's flat active-branch array and destroys all sibling
    // branches (edits/regenerations) on reload.
  });

  // Shared abort wiring for both interrupt-cancel seams: stop intent on
  // the transport (a long-lived owner that survives the inter-request
  // gap) plus the SDK's own `stop()`.
  const abortInterruptRun = {
    requestStop: () => stopTransport?.requestStop(),
    stop: () => chat.stop(),
  };

  // ESC / stop-button seam. assistant-ui's `onCancel` delegates to our
  // `stop` wrapper without settling the transcript, so this seam settles
  // the dangling calls itself (see `cancelPendingInterrupts`) with the AI
  // SDK's typed `output-error` state so the LLM is told the tool was
  // cancelled — no string sniffing anywhere downstream.
  const cancelInterrupts = (): Promise<PendingToolCall[]> =>
    cancelPendingInterrupts({
      messages: chat.messages,
      ...abortInterruptRun,
      resolveCancelled: ({ tool, toolCallId }) =>
        chat.addToolOutput({ tool, toolCallId, state: 'output-error', errorText: 'Tool execution was cancelled.' }),
    });

  // Shallow copy with overridden methods - avoids immer's deep proxy which fails on frozen arrays.
  //
  // `stop`/`sendMessage`/`regenerate` are the only user-initiated entry
  // points (assistant-ui calls `chat.stop()` on cancel, `chat.sendMessage`
  // on new/edit, `chat.regenerate` on reload). The SDK's loop continuation
  // calls `makeRequest` directly and never passes through them, so setting
  // stop intent here survives the loop while a genuine new turn clears it.
  const wrappedChat = {
    ...chat,
    stop: async (): Promise<void> => {
      // ESC / the stop button routes here. Unlike `sendMessage` (which is
      // stop-only because assistant-ui already settles/truncates), this
      // seam must settle the dangling interrupt itself so the trailing
      // assistant message reaches a terminal state and persists.
      const cancelled = await cancelInterrupts();
      if (cancelled.length === 0) {
        // No interrupt to cancel: ordinary stop.
        abortInterruptRun.requestStop();
        await abortInterruptRun.stop();
        return;
      }
      // No explicit persist needed: settling the dangling tool call via
      // `resolveCancelled` mutated the thread, which fires the snapshot
      // writer's subscription (`useThreadSnapshotPersistence`) — the full
      // branch tree, cancelled interrupt included, is persisted atomically.
    },
    sendMessage: async (...args: Parameters<typeof chat.sendMessage>): Promise<void> => {
      // A pending `requires-action` interrupt (e.g. `askUserQuestion`
      // awaiting `human()`) keeps the AI SDK run open; a superseding send
      // must abort it so the new turn starts clean instead of stacking on
      // the unresolved run.
      //
      // Stop-ONLY here (no `resolveCancelled`): on this seam assistant-ui
      // has already neutralised the pending call before our wrapper runs
      // — `onNew` settles it via `setMessages`, `onEdit`/`onReload`
      // truncate it out of the active branch. Re-settling would deref a
      // trailing message the edit just removed: a first-message edit
      // empties the branch, and `addToolOutput` then crashes on
      // `lastMessage.parts` (lastMessage `undefined`), taking the whole
      // thread down with it. The next run persists the settled state via
      // the normal path, so no explicit flush is needed here.
      await cancelPendingInterrupts({ messages: chat.messages, ...abortInterruptRun });
      stopTransport?.clearStopRequest();
      return chat.sendMessage(...args);
    },
    regenerate: async (...args: Parameters<typeof chat.regenerate>): Promise<void> => {
      stopTransport?.clearStopRequest();
      return chat.regenerate(...args);
    },
  };

  const runtime = useAISDKRuntime(wrappedChat, {
    // The history adapter persists/restores the FULL branch tree:
    // `useExternalHistory` imports an `ExportedMessageRepository` on
    // load (branches intact) and appends with the real branch-aware
    // `parentId` on each run. `id` is the same `threadListItem.id` the
    // session/binding namespace is keyed on, so the transcript and the
    // bindings projected from its active branch stay aligned.
    adapters: { ...adapters, history: historyAdapter },
    ...(toCreateMessage && { toCreateMessage }),
  });

  // The single persistence writer: subscribe to repository changes and
  // snapshot the full branch tree atomically. Ordinary turns, edits,
  // regenerations, and pending/cancelled interrupts are all thread changes,
  // so each is covered by construction — no per-seam eager flush needed.
  useThreadSnapshotPersistence({ runtime, historyAdapter });

  // Two-way binding: transport needs runtime reference for bidirectional communication
  if (transport instanceof AssistantChatTransport) {
    transport.setRuntime(runtime);
  }

  return runtime;
}
